import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { calendarApi } from './api';
import CalendarView from './CalendarView';
import { PanePicker } from './CalendarSideBySide';
import { addDays, todayWall } from './calendarTime';
import { CHANGED_EVENT, TODAY_RED, isTodayRed } from './calendarToday';
import { createChoicesFor, optionLabel } from './calendarPanes';
import {
  addYM, dayHover, entryHover, entryLabel, eventsByDay, monthName, monthTitle, monthWeeks, quadTitle, thisYM, thisYear,
} from './calendarGrid';

// =============================================================================
// 5.37.0 Quad and Year layouts: ONE calendar (chosen from the same pane options as Side by side — the server's rules;
// admins also get people's Personal) over several months, built as compact grids that fill the window with no page
// scrolling (sizes follow the window height, see calendar.css .idq / .idy). One /api/calendar/events call per
// range from the cache (no HDCal call per view change). Clicking a day opens CalendarDayDrill (Day view of that date,
// same calendar) with Back. The calendar choice is saved per login (cal.quad / cal.year); the start month / year
// always reopen on today's. Red = today's own-Personal events (as everywhere), otherwise the calendar's colour.
// Aa text size / Show all events do not apply here (compact by design).
// =============================================================================

const C = { panel: '#242424', border: '#333', text: '#f0f0f0', dim: '#aaa', violet: '#7c3aed', bg: '#1a1a1a' };
const navBtn = { padding: '4px 10px', border: '1px solid #444', borderRadius: 5, background: '#2f2f2f', color: '#f0f0f0', fontSize: 12, cursor: 'pointer' };
const field = { padding: '4px 6px', background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, colorScheme: 'dark' };
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW1 = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const NO_HIDDEN = new Set();

// Events of one calendar for [from, to): refetched on a range / calendar / reload change and after any calendar write.
function useRangeEvents(option, from, to, reloadKey) {
  const [state, setState] = useState({ events: [], error: '', loading: true });
  const [bump, setBump] = useState(0);
  useEffect(() => {
    const on = () => setBump((b) => b + 1);
    window.addEventListener(CHANGED_EVENT, on);
    return () => window.removeEventListener(CHANGED_EVENT, on);
  }, []);
  useEffect(() => {
    if (!option) return undefined;
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    calendarApi.events(`${from}T00:00:00`, `${to}T00:00:00`, option.user, option.calendar.id)
      .then((r) => { if (live) setState({ events: r.data.events, error: r.data.skipped ? `${r.data.skipped} item(s) could not be shown` : '', loading: false }); })
      .catch((e) => { if (live) setState({ events: [], error: e?.response?.data?.detail || 'Could not load events', loading: false }); });
    return () => { live = false; };
  }, [option && option.key, option && option.user, option && option.calendar.id, from, to, reloadKey, bump]);   // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

// Today's own-Personal events red (same rule as the other views), else the calendar's colour.
const colorOf = (e, color, myPersonalId, today) => (isTodayRed(e, myPersonalId, today) ? TODAY_RED : color);

function Bar({ options, option, onPick, testid, children, title, titleId }) {
  return (
    <div className="idmulti-bar">
      <PanePicker options={options} value={option.key} onChange={onPick} testid={testid} />
      {children}
      <div className="idmulti-title" data-testid={titleId}>{title}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
export function CalendarQuad({ options, value, onPick, me, color, reloadKey, onOpenDay }) {
  const option = options.find((o) => o.key === value) || options[0];
  const [start, setStart] = useState(thisYM);
  const months = useMemo(() => [0, 1, 2, 3].map((i) => addYM(start, i)), [start]);
  const weeks = useMemo(() => months.map((m) => monthWeeks(m)), [months]);
  const from = weeks[0][0][0];
  const last = weeks[3][weeks[3].length - 1];
  const to = addDays(last[6], 1);
  const { events, error } = useRangeEvents(option, from, to, reloadKey);
  const byDay = useMemo(() => eventsByDay(events, from, to), [events, from, to]);
  const today = todayWall();
  const myP = me?.personal_calendar_id || null;
  if (!option) return <div style={{ color: C.dim, fontSize: 13 }}>No calendars yet — they appear within a minute of the first sync.</div>;
  return (
    <div className="idq" data-testid="cal-quad" data-start={start}>
      <Bar options={options} option={option} onPick={onPick} testid="cal-quad-pick" title={quadTitle(start)} titleId="cal-quad-title">
        <button type="button" style={navBtn} onClick={() => setStart((s) => addYM(s, -1))} title="One month back" data-testid="cal-quad-prev">‹</button>
        <input type="month" value={start} onChange={(e) => { if (/^\d{4}-\d{2}$/.test(e.target.value)) setStart(e.target.value); }}
          style={field} data-testid="cal-quad-month" title="First month" />
        <button type="button" style={navBtn} onClick={() => setStart((s) => addYM(s, 1))} title="One month on" data-testid="cal-quad-next">›</button>
        <button type="button" style={navBtn} onClick={() => setStart(thisYM())} data-testid="cal-quad-today">Today</button>
        {error && <span style={{ color: '#fca5a5', fontSize: 12 }}>{error}</span>}
      </Bar>
      <div className="idq-grid">
        {months.map((m, mi) => (
          <div key={m} className="idq-month" data-testid="cal-quad-month-box" data-month={m}>
            <div className="idq-mtitle" style={{ borderTopColor: color }}>{monthTitle(m)}</div>
            <div className="idq-dow">{DOW.map((d) => <div key={d}>{d}</div>)}</div>
            <div className="idq-weeks" style={{ gridTemplateRows: `repeat(${weeks[mi].length}, minmax(0, 1fr))` }}>
              {weeks[mi].flat().map((d) => {
                const inMonth = d.slice(0, 7) === m;
                const list = inMonth ? (byDay[d] || []) : [];
                const more = list.length - 2;
                return (
                  <div key={d} className={`idq-day${inMonth ? '' : ' idq-out'}${d === today ? ' idq-today' : ''}`} data-date={inMonth ? d : undefined}
                    title={list.length > 2 ? dayHover(d, list) : undefined} onClick={() => inMonth && onOpenDay(d)}>
                    <div className="idq-dtop">
                      <span className="idq-dnum">{Number(d.slice(8))}</span>
                      {more > 0 && <span className="idq-more" data-testid="cal-quad-more">+{more}</span>}
                    </div>
                    {list.slice(0, 2).map((e) => {
                      const c = colorOf(e, color, myP, today);
                      return (
                        <div key={e.id} className={`idq-ev${c === TODAY_RED ? ' idq-red' : ''}`} title={entryHover(e)} style={{ '--c': c }} data-testid="cal-quad-ev">
                          {entryLabel(e)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
export function CalendarYear({ options, value, onPick, me, color, reloadKey, onOpenDay }) {
  const option = options.find((o) => o.key === value) || options[0];
  const [year, setYear] = useState(thisYear);
  const now = thisYear();
  const from = `${year}-01-01`;
  const to = `${year + 1}-01-01`;
  const { events, error } = useRangeEvents(option, from, to, reloadKey);
  const byDay = useMemo(() => eventsByDay(events, from, to), [events, from, to]);
  const today = todayWall();
  const myP = me?.personal_calendar_id || null;
  const years = [];
  for (let y = Math.min(year, now) - 10; y <= Math.max(year, now) + 10; y += 1) years.push(y);
  if (!option) return <div style={{ color: C.dim, fontSize: 13 }}>No calendars yet — they appear within a minute of the first sync.</div>;
  return (
    <div className="idy" data-testid="cal-year" data-year={year}>
      <Bar options={options} option={option} onPick={onPick} testid="cal-year-pick" title={String(year)} titleId="cal-year-title">
        <button type="button" style={navBtn} onClick={() => setYear((y) => y - 1)} title="Previous year" data-testid="cal-year-prev">‹</button>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={field} data-testid="cal-year-select">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button type="button" style={navBtn} onClick={() => setYear((y) => y + 1)} title="Next year" data-testid="cal-year-next">›</button>
        <button type="button" style={navBtn} onClick={() => setYear(thisYear())} data-testid="cal-year-today">Today</button>
        {error && <span style={{ color: '#fca5a5', fontSize: 12 }}>{error}</span>}
      </Bar>
      <div className="idy-grid">
        {Array.from({ length: 12 }, (_, i) => addYM(`${year}-01`, i)).map((m) => (
          <div key={m} className="idy-month" data-testid="cal-year-month" data-month={m}>
            <div className="idy-mtitle">{monthName(m)}</div>
            <div className="idy-dow">{DOW1.map((d, i) => <div key={i}>{d}</div>)}</div>
            <div className="idy-weeks">
              {monthWeeks(m, true).flat().map((d) => {
                if (d.slice(0, 7) !== m) return <div key={d} className="idy-day idy-out" />;
                const list = byDay[d] || [];
                const red = list.some((e) => isTodayRed(e, myP, today));
                const c = red ? TODAY_RED : color;
                return (
                  <div key={d} className={`idy-day${d === today ? ' idy-today' : ''}${list.length ? ' idy-has' : ''}`} data-date={d}
                    data-count={list.length} title={list.length ? dayHover(d, list) : undefined} onClick={() => onOpenDay(d)}>
                    <span className="idy-dnum">{Number(d.slice(8))}</span>
                    {list.length === 1 && <span className="idy-dot" style={{ background: c }} data-testid="cal-year-dot" />}
                    {list.length > 1 && <span className="idy-count" style={{ background: c }} data-testid="cal-year-count">{list.length}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// A day opened from Quad / Year: Day view of that date, the same calendar (create / edit as in a side-by-side pane).
export function CalendarDayDrill({ options, value, me, color, date, from, onBack, reloadKey, onChanged }) {
  const option = options.find((o) => o.key === value) || options[0];
  const api = useRef(null);
  const [title, setTitle] = useState('');
  const choices = useMemo(() => createChoicesFor(option, options), [option, options]);
  const ov = useMemo(() => ({ me, calendars: [option.calendar, ...choices.filter((c) => c.id !== option.calendar.id)] }), [me, option, choices]);
  const colors = useMemo(() => ({ [option.calendar.id]: color }), [option, color]);
  const onDatesSet = useCallback((arg) => setTitle(arg.view.title), []);
  const go = (fn) => { if (api.current) fn(api.current); };
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }} data-testid="cal-drill" data-date={date}>
      <div className="idmulti-bar">
        <button type="button" style={{ ...navBtn, background: '#0e7490', borderColor: '#0e7490', fontWeight: 600 }} onClick={onBack} data-testid="cal-drill-back">
          ← Back to {from === 'year' ? 'Year' : 'Quad'}
        </button>
        <span style={{ width: 11, height: 11, borderRadius: 3, background: color }} />
        <span style={{ fontWeight: 700, fontSize: 13 }} data-testid="cal-drill-name">{optionLabel(option)}</span>
        <button type="button" style={navBtn} onClick={() => go((a) => a.prev())} data-testid="cal-drill-prev">‹</button>
        <button type="button" style={navBtn} onClick={() => go((a) => a.next())} data-testid="cal-drill-next">›</button>
        <button type="button" style={navBtn} onClick={() => go((a) => a.today())} data-testid="cal-drill-today">Today</button>
        <div className="idmulti-title" data-testid="cal-drill-title">{title}</div>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <CalendarView target={option.user} overview={ov} colors={colors} hidden={NO_HIDDEN} reloadKey={reloadKey} onChanged={onChanged}
          calendarId={option.calendar.id} paneChoices={choices} initialView="timeGridDay" initialDate={new Date(`${date}T00:00:00Z`)}
          onDatesSet={onDatesSet} onApi={(a) => { api.current = a; }} />
      </div>
    </div>
  );
}
